import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core';
import axios from 'axios';
import { ReactNode, useEffect, useMemo, useRef } from 'react';
// Analytics
import {
  FILE_DOWNLOADED,
  FILE_MANAGER_EVENT,
  INDIVIDUAL_DOWNLOAD_CLICK,
  ITEM_CLICKED,
  trackEvent,
} from 'src/analytics';
// Components
import { faArrowDownToLine } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { colors } from 'design-system';
// Models
import { RequestIndicatorTypes } from 'data-layer-cr/dist/models';
// Redux
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from 'src/store/rootReducer';

export const useStyle = makeStyles(() => ({
  documentText: {
    fontSize: 17,
    fontWeight: 500,
  },
  divider: {
    height: 1,
    backgroundColor: colors.gallery,
    marginTop: 25,
    marginBottom: 25,
  },
}));

export const DocumentRow = ({
  children,
  currentFileDownload,
  downloadStatus,
  fileName,
  title,
  onClick,
}: Props): JSX.Element => {
  const classes = useStyle();
  const cancelTokenSource = useRef(axios.CancelToken.source()).current;

  const downloadingFile = useMemo(
    () => currentFileDownload === fileName && downloadStatus === 'requesting',
    [downloadStatus, currentFileDownload, fileName],
  );

  // make sure to only send request download once. Subsequent ones should simply refer the redux store
  function handleDocumentDowload(): void {
    trackEvent({
      eventName: FILE_MANAGER_EVENT,
      eventProperties: {
        [ITEM_CLICKED]: INDIVIDUAL_DOWNLOAD_CLICK,
        [FILE_DOWNLOADED]: fileName,
      },
    });
    onClick(fileName);
  }

  // cleanup list on unmount
  useEffect(
    () => {
      return (): void => {
        cancelTokenSource.cancel();
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <>
      <Grid container justifyContent="space-between">
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ minWidth: 42 }}>{children}</Box>
          <Typography className={classes.documentText}>{title}</Typography>
        </Box>
        <Box
          sx={{
            color:
              downloadStatus === 'requesting'
                ? colors.gallery
                : colors.bright_turquoise,
          }}
        >
          <IconButton
            data-testid="download-button"
            onClick={handleDocumentDowload}
            disabled={downloadStatus === 'requesting'}
            color="inherit"
          >
            {downloadingFile ? (
              <CircularProgress
                size={20}
                data-testid="button-loading"
                color="inherit"
              />
            ) : (
              <FontAwesomeIcon
                icon={faArrowDownToLine}
                fontSize={24}
                color={
                  downloadStatus === 'requesting'
                    ? colors.gallery
                    : colors.bright_turquoise
                }
              />
            )}
          </IconButton>
        </Box>
      </Grid>
      <Divider className={classes.divider} data-testid="row-divider" />
    </>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  currentFileDownload: string;
  downloadStatus: RequestIndicatorTypes;
} => ({
  currentFileDownload: state.policy.currentFileDownload,
  downloadStatus: state.policy.downloadStatus,
});

const reduxActions = {};

const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  title: string;
  fileName: string;
  children: ReactNode;
  onClick: (fileName: string) => void;
}

export default connector(DocumentRow);
